import Image from 'next/image'
import {FC} from 'react'

import {useBrandMonitoringContent} from '@/content/brand-monitoring/brandMonitoring'
import {useTranslation} from '@/i18n'
import {theme} from '@/theme/theme'
import {BrandSubscriptionPlatform} from '@/utils/brand-subscription/constants'
import {MOBILE_SCREENSIZE, USER_TABLET_SCREENSIZE} from '@/utils/styles/breakpoints'
import {analytics} from '@/utils/tracking/analytics'

import {Section} from '../../common/content/Section'
import {SectionLayout} from '../../common/content/SectionLayout'
import {Text, TextVariant} from '../../common/content/Text'
import {ArrowIcon} from '../../common/icons/ArrowIcon'
import {MonitoringPlatformIcon} from '../../common/icons/MonitoringPlatformIcon'
import {BrandMonitoringIntroVideo} from '../video/BrandMonitoringIntroVideo'

const iconSize = 40
const numOfVisiblePlatforms = 8

const defaultMonitoringPlatforms = [
  BrandSubscriptionPlatform.facebook,
  BrandSubscriptionPlatform.instagram,
  BrandSubscriptionPlatform.youtube,
  BrandSubscriptionPlatform.appStore,
  BrandSubscriptionPlatform.googlePlay,
  BrandSubscriptionPlatform.amazon,
  BrandSubscriptionPlatform.etsy,
  BrandSubscriptionPlatform.aliExpress,
]

type BrandProtectionOverviewProps = {
  platforms?: BrandSubscriptionPlatform[]
  industryName?: string
  videoTrackedEventParameters?: Record<string, string>
  backgroundColor?: string
}

export const BrandProtectionOverview: FC<BrandProtectionOverviewProps> = ({
  platforms = defaultMonitoringPlatforms,
  industryName,
  videoTrackedEventParameters,
  backgroundColor = theme.palette.background.butter,
}) => {
  const {t} = useTranslation()
  const {title, description, subtitle, moreIcon, button} = useBrandMonitoringContent().overview

  return (
    <>
      <Section hasPaddingTop hasPaddingBottom backgroundColor={backgroundColor}>
        <SectionLayout
          title={{text: t(title)}}
          description={{text: t(description)}}
          align="left"
          orientation="horizontal"
          additionalContent={
            <div className="content">
              <Text variant={TextVariant.h5} element="p" color={theme.palette.text.light}>
                {t(subtitle)}
              </Text>
              <div className="platforms">
                {platforms.slice(0, numOfVisiblePlatforms).map((platform) => (
                  <MonitoringPlatformIcon key={platform} platform={platform} size={iconSize} />
                ))}
                <div className="platform-placeholder">
                  <Image
                    src={moreIcon.imageSrc}
                    alt={moreIcon.imageAlt}
                    width={iconSize}
                    height={iconSize}
                  />
                </div>
              </div>
            </div>
          }
          buttons={[
            {
              text: t(button.text),
              linkHref: button.linkHref,
              icon: <ArrowIcon color={theme.palette.text.white} />,
              isIconAnimated: true,
              onClick: () =>
                analytics.track('Discover brand monitoring button clicked', {
                  section: 'Brand protection',
                  ...(industryName ? {industry: industryName} : {}),
                }),
            },
          ]}
          maxWidth="545px"
          verticalAlign="top"
        >
          <div className="video-wrapper">
            <BrandMonitoringIntroVideo
              trackedEventParameters={videoTrackedEventParameters}
              sizes={`${MOBILE_SCREENSIZE} 100vw, 540px`}
            />
          </div>
        </SectionLayout>
      </Section>

      <style jsx>{`
        .content {
          margin-top: ${theme.spacing[4]};
        }
        .platforms {
          display: flex;
          gap: ${theme.spacing[2.5]};
          margin-top: ${theme.spacing[3]};
        }
        .platform-placeholder {
          display: flex;
        }
        .video-wrapper {
          width: 100%;
          height: 100%;
          max-width: 540px;
          margin-left: ${theme.spacing[5.5]};
          margin-top: ${theme.spacing[2]};
        }
        @media ${USER_TABLET_SCREENSIZE} {
          .video-wrapper {
            margin-left: 0;
            margin-top: ${theme.spacing[6]};
          }
        }
        @media ${MOBILE_SCREENSIZE} {
          .platforms {
            flex-wrap: wrap;
            justify-content: center;
          }
        }
      `}</style>
    </>
  )
}
